import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';

interface SearchBarProps {
  onSearch: (word: string) => void;
  onInputChange: (word: string) => void;
  isLoading?: boolean;
  suggestions: string[];
  wordParam?: string;
  onSuggestionSelect: (word: string) => void;
  setWordContext?: (word: string) => void;
  onCancel?: () => void;
}

export default function SearchBar({ 
  onSearch, 
  onInputChange,
  isLoading = false, 
  suggestions,
  wordParam,
  onSuggestionSelect,
  setWordContext,
  onCancel,
}: SearchBarProps) {
  const [word, setWord] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const [focusedSuggestionIndex, setFocusedSuggestionIndex] = useState(-1);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (wordParam) {
      setWord(wordParam);
    }
  }, [wordParam]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (word.trim()) {
      onSearch(word.trim());
      setShowSuggestions(false);
      setFocusedSuggestionIndex(-1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setWord(value);
    setShowSuggestions(value.length > 0);
    setFocusedSuggestionIndex(-1);
    onInputChange(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedSuggestionIndex((prevIndex) => 
        prevIndex < suggestions.length - 1 ? prevIndex + 1 : prevIndex
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedSuggestionIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : -1));
    } else if (e.key === 'Enter' && focusedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[focusedSuggestionIndex]);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if(setWordContext) {
      setWordContext(suggestion)
    }
    setWord(suggestion);
    onSuggestionSelect(suggestion);
    setShowSuggestions(false);
    setFocusedSuggestionIndex(-1);
  };

  return (
    <form onSubmit={handleSubmit} className="w-full relative">
      <div className="relative">
        <input
          type="text"
          value={word}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (word.length > 0) setShowSuggestions(true);
          }}
          placeholder="Search for a word in english"
          className="w-full px-4 py-3 pl-12 pr-12 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
          data-testid="search-input"
        />
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        {isLoading ? (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500" />
            <button
              type="button"
              onClick={() => {
                if (onCancel) onCancel();
                setWord('');
                onInputChange('');
              }}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
              title="Cancel search"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          word && (
            <button
              type="button"
              onClick={() => {
                setWord('');
                onInputChange('');
                setShowSuggestions(false);
              }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
              title="Clear input"
            >
              <X className="w-5 h-5" />
            </button>
          )
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={() => setFocusedSuggestionIndex(index)}
              className={`w-full text-left px-4 py-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors ${
                index === focusedSuggestionIndex ? 'bg-gray-100' : ''
              }`}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </form>
  );
}